import React, { FC, useEffect, useMemo, useState } from 'react';
import { Col, ConfigProvider, Layout, Row } from 'antd';
import { PageHeader } from 'common/components/Page/PageHeader';
import { PageContent } from 'common/components/Page/PageContent';
import { PageStoreSelector } from 'common/components/PageStoreSelector';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  getTrackStoreNotificationCollection,
  getTrackStoreStoreCollection,
  getTrackStoreSummary,
  getTrackStoreWeeklyGrid,
} from 'app/store/reducers/trackStore.reducer';
import { TrackStoreOverall } from 'entities/TrackStore/components/TrackStoreOverall';
import { TrackStoreNotifications } from 'entities/TrackStore/components/TrackStoreNotifications';
import { TrackStoreStoreCards } from 'entities/TrackStore/components/TrackStoreStoreCards';

export const TrackStorePage: FC = () => {
  const dispatch = useAppDispatch();

  const { trackStoreStoreCollection } = useAppSelector((state) => state.trackStore);

  const [storeCode, setStoreCode] = useState<string | number | undefined>();
  const [isNotificationsExpanded, setIsNotificationsExpanded] = useState<boolean>(false);

  const selectedStore = useMemo(() => {
    return trackStoreStoreCollection?.data.find((item) => item.code === storeCode);
  }, [trackStoreStoreCollection?.data, storeCode]);

  useEffect(() => {
    dispatch(getTrackStoreStoreCollection());
  }, []);

  useEffect(() => {
    dispatch(
      getTrackStoreSummary({
        storeCode: selectedStore?.code,
        location: selectedStore?.location,
      })
    );
    dispatch(
      getTrackStoreWeeklyGrid({
        storeCode: selectedStore?.code,
        location: selectedStore?.location,
      })
    );
    dispatch(
      getTrackStoreNotificationCollection({
        storeCode: selectedStore?.code,
        location: selectedStore?.location,
      })
    );
  }, [selectedStore]);

  const changeStore = (value?: string | number): void => {
    setStoreCode(value);
  };

  return (
    <ConfigProvider theme={{ token: { screenXXLMin: 1920, screenXXL: 1920 } }}>
      <Layout className="relative height-full flex flex__column">
        <PageHeader
          title={
            <div>
              <span>Track Store Real-Time</span>
              {trackStoreStoreCollection && (
                <span className="ml-3 ff-regular fs-xxs color-gray-1">{`${trackStoreStoreCollection.meta.count} stores`}</span>
              )}
            </div>
          }
          extra={
            <Row>
              <Col>
                <PageStoreSelector stores={trackStoreStoreCollection?.data} onChange={changeStore} />
              </Col>
            </Row>
          }
        />

        <PageContent>
          <Row gutter={16} className="flex__item_1 width-full" style={{ minHeight: 0 }}>
            <Col span={isNotificationsExpanded ? 12 : 24} className="flex flex__column flex__item_1 height-full">
              <div className="flex flex__column flex__item_1 page-content__scroll-container scrollbar-none">
                <Row gutter={16} className={`mb-5 ml-5 ${isNotificationsExpanded ? '' : 'mr-5'}`}>
                  <Col sm={17} xxl={isNotificationsExpanded ? 24 : 12}>
                    <TrackStoreOverall />
                  </Col>

                  {!isNotificationsExpanded && (
                    <Col sm={7} xxl={12}>
                      <TrackStoreNotifications
                        storeCode={storeCode}
                        expandable={{
                          isExpanded: isNotificationsExpanded,
                          onIsExpandedChange: setIsNotificationsExpanded,
                        }}
                      />
                    </Col>
                  )}
                </Row>

                <div className="flex__item_1 flex flex__column">
                  <TrackStoreStoreCards store={selectedStore} />
                </div>
              </div>
            </Col>

            {isNotificationsExpanded && (
              <Col span={12} className="flex height-full">
                <Row gutter={16} className="mr-5 flex__item_1 height-full">
                  <Col span={24} className="pb-5 height-full">
                    <TrackStoreNotifications
                      storeCode={storeCode}
                      expandable={{
                        isExpanded: isNotificationsExpanded,
                        onIsExpandedChange: setIsNotificationsExpanded,
                      }}
                    />
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
        </PageContent>
      </Layout>
    </ConfigProvider>
  );
};
